import store from "@/store";
import router from "@/router";
import {
  ref,
  reactive,
  computed,
  onBeforeMount,
  watch,
} from "@vue/composition-api";
import useFlatPickr from "@/utils/hooks/useFlatPickr";
import useValidationModal from "@/@core/components/base-modal/hooks/useValidationModal";

import { articleModel, articleCategoryModel } from "@/models/article/article";

export default function useArticleCreateEditForm(props) {
  const {
    fetchArticleDetailModel,
    createArticleModel,
    editArticleModel,
    fetchInventoryModel,
    fetchRewardModel,
  } = articleModel();
  const { fetchArticleCategoryListModel } = articleCategoryModel();
  const {
    openSuccessModal,
    openDefaultErrorModal,
    openErrorModal,
  } = useValidationModal();
  const keyChange = ref(0);

  const form = reactive({
    seq: null,
    name: null,
    category: null,
    status: true,
    detail: "",
    detailEn: "",
    valid_from: { date: null, time: null },
    valid_through: { date: null, time: null },
    image_1: { file: null, url: null },
    image_2: { file: null, url: null },
    tierFlag: [],
    previlege_type_id: 2,
    condition: 1,
    inventory: null,
    inventoryRef: null,
    rewardAlltier: null,
    pointAlltier: null,
    table: [],
    isVideo: false,
    videoTag: null,
    remark: null,
    reference1: null,
    reference2: null,
    reference3: null,
    reference4: null,
  });

  const loading = reactive({
    api: false,
    createOrEdit: false,
  });

  const articleCategory = reactive({
    payload: [],
  });

  const rewardTypeOptions = ref([
    { text: "Point", value: 2 },
    { text: "Privilege", value: 1 },
    { text: "None", value: 3 },
  ]);

  const pointConditionOptions = ref([
    { text: "สมาชิกทุกระดับได้แต้มเท่ากัน", value: 1 },
    { text: "สมาชิกทุกระดับได้แต้มไม่เท่ากัน", value: 2 },
  ]);

  const rewardConditionOptions = ref([
    { text: "สมาชิกทุกระดับได้ของรางวัลเหมือนกัน", value: 1 },
    { text: "สมาชิกทุกระดับได้ของรางวัลไม่เหมือนกัน", value: 2 },
  ]);

  const rewardSettingOption = reactive({
    inventory: [],
    all: [],
  });

  const fieldsRewardTable = [
    { key: "seq", label: "No." },
    {
      key: "name_th",
      label: "Reward name(TH)",
    },
    {
      key: "name_en",
      label: "Reward name(EN)",
    },
    { key: "banner", label: "Banner" },
  ];

  const {
    flatpickrDateConfig,
    flatpickrTimeConfig,
    validFrom,
    validThrough,
    onOpenValidFromDatePicker,
    onSelectValidFromDate,
    onOpenValidFromTimePicker,
    onOpenValidThroughTimePicker,
    onSelectValidFromTimePicker,
  } = useFlatPickr();

  const fetchArticleCategory = (loadingAction = true) => {
    if (loadingAction) {
      loading.api = true;
    }
    fetchArticleCategoryListModel.payload.Filters = [
      {
        Seq: 1,
        Key: "Status",
        Value: 1,
      },
    ];
    store
      .dispatch("api/apiRequest", fetchArticleCategoryListModel)
      .then((response) => {
        articleCategory.payload = response.data.payload;
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {});
  };

  const fetchInventory = (loadingAction = true) => {
    window.$loading(true);
    if (loadingAction) {
      loading.api = true;
    }
    store
      .dispatch("api/apiRequest", fetchInventoryModel)
      .then((response) => {
        rewardSettingOption.inventory = response.data;
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        window.$loading(false);
        loading.api = false;
      });
  };

  const fetchRewardOption = (loadingAction = true) => {
    window.$loading(true);
    if (loadingAction) {
      loading.api = true;
    }
    fetchRewardModel.payload.Inventory_Ref = form.inventoryRef;
    store
      .dispatch("api/apiRequest", fetchRewardModel)
      .then((response) => {
        rewardSettingOption.all = response.data;
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        window.$loading(false);
        loading.api = false;
      });
  };

  const fetchArticleDetail = () => {
    window.$loading(true);
    loading.api = true;
    const articleId = router.currentRoute.params.id;

    fetchArticleDetailModel.payload.Article_Id = articleId;

    store
      .dispatch("api/apiRequest", fetchArticleDetailModel)
      .then((response) => {
        const articleInfo = response.data.article;
        const privilegeInfo = response.data.privilege;
        const criteriaInfo = response.data.criteria;
        form.seq = articleInfo.seq;
        form.name = articleInfo.article_title;
        form.category = articleInfo.category_id?.category_id;
        form.status = articleInfo.status == 1;
        form.detail =
          articleInfo.article_content_th 
            ? articleInfo.article_content_th
                .replaceAll('"<video', '"&lt;video')
                .replaceAll('</video>"', '&lt;/video&gt;"')
                .replaceAll("<source", "&lt;source")
            : null;
        form.detailEn =
          articleInfo.article_content_en 
            ? articleInfo.article_content_en
                .replaceAll('"<video', '"&lt;video')
                .replaceAll('</video>"', '&lt;/video&gt;"')
                .replaceAll("<source", "&lt;source")
            : null;
        form.image_1.url = articleInfo.article_cover;
        form.image_2.url = articleInfo.square_image_url;
        validFrom.date = resolveDate(articleInfo.valid_from);
        validFrom.time = resolveTime(articleInfo.valid_from);
        validThrough.date = resolveDate(articleInfo.valid_through);
        validThrough.time = resolveTime(articleInfo.valid_through);
        form.previlege_type_id = privilegeInfo.type;
        form.condition = 1; // To DO membership
        form.isVideo = articleInfo.iscontent_video;
        if (form.previlege_type_id == 1) {
          // privilege
          form.inventory = criteriaInfo.inventory_id;
          form.inventoryRef = criteriaInfo.inventory_ref;
          form.rewardAlltier = criteriaInfo.rewards?.reward_name_th;
          form.table.push({
            seq: 1,
            name_th: criteriaInfo.rewards?.reward_name_th,
            name_en: criteriaInfo.rewards?.reward_name_en,
            banner: criteriaInfo.rewards?.reward_image_url,
          });
        } else if (form.previlege_type_id == 2) {
          // point
          form.pointAlltier = response.data.criteria.point;
        }
        form.remark = articleInfo.remark
        form.reference1 = articleInfo.reference1
        form.reference2 = articleInfo.reference2
        form.reference3 = articleInfo.reference3
        form.reference4 = articleInfo.reference4
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        window.$loading(false);
        loading.api = false;
      });
  };

  const createArticle = () => {
    window.$loading(true);
    loading.createOrEdit = true;

    createArticleModel.payload.Article_Title = form.name;
    createArticleModel.payload.Category_Id = form.category;
    createArticleModel.payload.Status = form.status ? 1 : 0;
    createArticleModel.payload.Article_Cover_Url = form.image_1.file;
    createArticleModel.payload.Square_Image_Url = form.image_2.file;
    createArticleModel.payload.Article_Content_TH = form.detail
      ? form.detail.replaceAll("&lt;", "<").replaceAll("&gt;", ">")
      : null;
    createArticleModel.payload.Article_Content_EN = form.detailEn
      ? form.detailEn.replaceAll("&lt;", "<").replaceAll("&gt;", ">")
      : null;
    createArticleModel.payload.Valid_From = resolveSubmitDateString(
      validFrom.date
    );
    createArticleModel.payload.Valid_Through = resolveSubmitDateString(
      validThrough.date
    );
    createArticleModel.payload.Valid_From_Time = validFrom.time;
    createArticleModel.payload.Valid_Through_Time = validThrough.time;
    createArticleModel.payload.Privilege_Type = form.previlege_type_id;
    if (form.previlege_type_id == 1) {
      // privilege
      createArticleModel.payload[`Rewards[${0}].Inventory_Id`] = form.inventory;
      createArticleModel.payload[`Rewards[${0}].Privilege_Id`] =
        form.rewardAlltier.reward_id;
      form.table.push({});
    } else if (form.previlege_type_id == 2) {
      // point
      createArticleModel.payload[`Points[${0}].Point`] = form.pointAlltier;
    }
    createArticleModel.payload.Remark = form.remark
    createArticleModel.payload.Reference1 = form.reference1
    createArticleModel.payload.Reference2 = form.reference2
    createArticleModel.payload.Reference3 = form.reference3
    createArticleModel.payload.Reference4 = form.reference4
    store
      .dispatch("api/apiFormRequest", createArticleModel)
      .then(() => {
        loading.createOrEdit = false;
        openSuccessModal(null, null, null, () =>
          router.push("/article/article/list")
        );
      })
      .catch((err) => {
        openDefaultErrorModal(err);
        loading.createOrEdit = false;
      })
      .finally(() => {
        window.$loading(false);
      });
  };

  const editArticle = () => {
    window.$loading(true);
    loading.createOrEdit = true;

    const articleId = parseInt(router.currentRoute.params.id);

    editArticleModel.payload.Article_Id = articleId;
    editArticleModel.payload.Seq = form.seq;
    editArticleModel.payload.Article_Title = form.name;
    editArticleModel.payload.Category_Id = form.category;
    editArticleModel.payload.Status = form.status ? 1 : 0;
    editArticleModel.payload.Article_Cover_Url = form.image_1.file;
    editArticleModel.payload.Square_Image_Url = form.image_2.file;
    editArticleModel.payload.Article_Content_TH = form.detail
      ? form.detail.replaceAll("&lt;", "<").replaceAll("&gt;", ">")
      : null;
    editArticleModel.payload.Article_Content_EN = form.detailEn
      ? form.detailEn.replaceAll("&lt;", "<").replaceAll("&gt;", ">")
      : null;
    editArticleModel.payload.Valid_From = resolveSubmitDateString(
      validFrom.date
    );
    editArticleModel.payload.Valid_Through = resolveSubmitDateString(
      validThrough.date
    );
    editArticleModel.payload.Valid_From_Time = validFrom.time;
    editArticleModel.payload.Valid_Through_Time = validThrough.time;
    editArticleModel.payload.Remark = form.remark
    editArticleModel.payload.Reference1 = form.reference1
    editArticleModel.payload.Reference2 = form.reference2
    editArticleModel.payload.Reference3 = form.reference3
    editArticleModel.payload.Reference4 = form.reference4

    store
      .dispatch("api/apiFormRequest", editArticleModel)
      .then(() => {
        loading.createOrEdit = false;
        openSuccessModal(null, null, null, () =>
          router.push("/article/article/list")
        );
      })
      .catch((err) => {
        openDefaultErrorModal(err);
        loading.createOrEdit = false;
      })
      .finally(() => {
        window.$loading(false);
      });
  };

  const resolveSubmitDateString = (date) => {
    const splitDate = date.split("-");

    const reverseDate = splitDate.reverse();

    return reverseDate.join("-");
  };

  const onSubmitForm = () => {
    if (!form.image_1.url || !form.image_2.url || form.detail == "") {
      return openErrorModal("กรอกข้อมูลไม่ครบ", undefined, "กลับไปแก้ไข");
    }
    if (props?.isEdit) {
      editArticle();
    } else {
      createArticle();
    }
  };

  const resolveDate = (dateInput) => {
    const date = new Date(dateInput);
    let dateStr = "";

    try {
      dateStr = `${date.toLocaleString("default", {
        day: "2-digit",
      })}-${date.toLocaleString("default", {
        month: "2-digit",
      })}-${date.getFullYear()}`;
    } catch (err) {}

    return dateStr;
  };

  const resolveTime = (timeInput) => {
    const time = new Date(timeInput);

    try {
      const hours = time.getHours();
      const minutes = time.getMinutes();

      const timeStr = `${hours < 10 ? `0${hours}` : hours}:${
        minutes < 10 ? `0${minutes}` : minutes
      }`;

      return timeStr;
    } catch (err) {
      return "";
    }
  };

  const onChangeVideoTag = (url) => {
    const videoTag = `"&lt;video id="article-video" class="pointer d-block" playsinline="true"&gt;&lt;source src="${url}"/&gt;&lt;/video&gt;"`;
    const combine = form.detail + videoTag;
    form.videoTag = "";
    return (form.detail = combine);
  };

  const onUploadCoverImage = (image) => {
    form.image_1.file = image.file;
    form.image_1.url = image.url;
  };

  const onUploadSquareImage = (image) => {
    form.image_2.file = image.file;
    form.image_2.url = image.url;
  };

  const onSelectInventory = (item) => {
    if(!item.inventoryref && !item.inventoryid) return 
    form.inventoryRef = item.inventoryref;
    form.inventory = item.inventoryid;
    form.rewardAlltier = null;
    form.table = [];
    if (form.inventoryRef) {
      return fetchRewardOption(false);
    }
  };

  const renderRewardTable = (reward) => {
    const selectedReward = { ...reward };
    form.table = [];
    form.table.push(selectedReward);
    return keyChange.value++;

    // if (!reward) {
    //   return (form.table = form.table.filter(
    //     reward => reward.tier.tier_id !== tier.tier_id,
    //   ))
    // }
    // const selectedReward = { ...reward }
    // const index = form.table.findIndex(
    //   reward => reward.tier.tier_id == tier.tier_id,
    // )
    // selectedReward.tier = tier
    // if (index < 0) {
    //   return form.table.push(selectedReward)
    // }
    // form.table[index] = selectedReward
    // return keyChange.value++
  };

  const onChangeCondition = () => {
    if (form.previlege_type_id == 3) {
      form.condition = 1;
    }
    form.inventory = null;
    form.rewardAlltier = null;
    form.pointAlltier = null;
    return (form.table = []);
  };

  const resolveStatusLabel = (status) => {
    switch (status) {
      case true:
        return "Active";
      case false:
        return "Inactive";
      default:
        return "Unknown";
    }
  };

  const resolveStatusClass = (status) => {
    switch (status) {
      case true:
        return "active-icon";
      case false:
        return "inActive-icon";
      default:
        return "Unknown";
    }
  };

  onBeforeMount(() => {
    fetchArticleCategory();
    if (props?.isEdit) {
      fetchArticleDetail();
    } else {
      fetchInventory();
    }
  });

  watch(
    () => [form.detail, form.detailEn],
    () => {
      window.$setAttrLoadingLazy();
    }
  );

  return {
    keyChange,
    loading,
    form,
    flatpickrDateConfig,
    flatpickrTimeConfig,
    validFrom,
    validThrough,
    articleCategory,
    rewardTypeOptions,
    pointConditionOptions,
    rewardConditionOptions,
    rewardSettingOption,
    fieldsRewardTable,
    onChangeVideoTag,
    onChangeCondition,
    renderRewardTable,
    onSelectInventory,
    onUploadCoverImage,
    onUploadSquareImage,
    onOpenValidFromDatePicker,
    onOpenValidFromTimePicker,
    onSelectValidFromDate,
    onSelectValidFromTimePicker,
    onOpenValidThroughTimePicker,
    onSubmitForm,
    resolveStatusClass,
    resolveStatusLabel,
  };
}
